import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trophy as Whistle, Plus, ArrowRight } from "@phosphor-icons/react";

export default function CoachArea() {
    const { user } = useAuth();
    const [matches, setMatches] = useState([]);
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [form, setForm] = useState({
        opponent: "",
        home: true,
        date: "",
        kickoff_time: "10:30",
        meeting_time: "09:00",
        field_name: "",
        materials: "Maglia, Pantaloncini, Calzettoni, Borraccia",
        notes: "",
    });
    const [comm, setComm] = useState({ title: "", body: "" });
    const [selectedMatch, setSelectedMatch] = useState("");
    const [selectedPlayers, setSelectedPlayers] = useState({});

    const load = async () => {
        const [m, p, t] = await Promise.all([
            api.get("/matches"),
            api.get("/players"),
            api.get("/teams"),
        ]);
        setMatches(m.data);
        setPlayers(p.data);
        setTeams(t.data);
    };
    useEffect(() => {
        load();
    }, []);

    const teamName = teams.find((t) => t.id === user?.team_id)?.name || "-";

    const createMatch = async (e) => {
        e.preventDefault();
        try {
            await api.post("/matches", {
                team_id: user.team_id,
                opponent: form.opponent,
                home: form.home,
                date: form.date,
                kickoff_time: form.kickoff_time,
                meeting_time: form.meeting_time,
                field_name: form.field_name || null,
                materials: form.materials
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                notes: form.notes,
            });
            toast.success("Partita creata");
            setForm({ ...form, opponent: "", date: "", notes: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const publishComm = async (e) => {
        e.preventDefault();
        try {
            await api.post("/communications", { ...comm, team_id: user.team_id });
            setComm({ title: "", body: "" });
            toast.success("Comunicazione inviata");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        if (!selectedMatch) return;
        (async () => {
            const { data } = await api.get(
                `/matches/${selectedMatch}/convocations`
            );
            const sel = {};
            data.forEach((c) => (sel[c.player_id] = true));
            setSelectedPlayers(sel);
        })();
    }, [selectedMatch]);

    const saveConvocations = async () => {
        const ids = Object.entries(selectedPlayers)
            .filter(([, v]) => v)
            .map(([k]) => k);
        try {
            await api.post(`/matches/${selectedMatch}/convocations`, {
                player_ids: ids,
            });
            toast.success("Convocazioni aggiornate");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="coach-area">
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
                    <Whistle size={14} weight="fill" /> {teamName}
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
                    Area Allenatore
                </h1>
            </div>

            <section>
                <SectionTitle>Nuova partita</SectionTitle>
                <form
                    onSubmit={createMatch}
                    className="rounded-2xl hairline bg-card p-4 grid gap-3 md:grid-cols-4 mt-3"
                >
                    <Input
                        placeholder="Avversaria"
                        value={form.opponent}
                        onChange={(e) => setForm({ ...form, opponent: e.target.value })}
                        required
                    />
                    <Input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        required
                    />
                    <Input
                        type="time"
                        value={form.meeting_time}
                        onChange={(e) => setForm({ ...form, meeting_time: e.target.value })}
                    />
                    <Input
                        type="time"
                        value={form.kickoff_time}
                        onChange={(e) => setForm({ ...form, kickoff_time: e.target.value })}
                    />
                    <Select
                        value={form.home ? "home" : "away"}
                        onValueChange={(v) => setForm({ ...form, home: v === "home" })}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="home">Casa</SelectItem>
                            <SelectItem value="away">Trasferta</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input
                        placeholder="Campo"
                        value={form.field_name}
                        onChange={(e) => setForm({ ...form, field_name: e.target.value })}
                        className="md:col-span-2"
                    />
                    <Button type="submit" className="press">
                        <Plus size={14} className="mr-2" /> Crea
                    </Button>
                    <Input
                        placeholder="Materiale (virgole)"
                        value={form.materials}
                        onChange={(e) => setForm({ ...form, materials: e.target.value })}
                        className="md:col-span-4"
                    />
                    <Textarea
                        placeholder="Note"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        className="md:col-span-4"
                    />
                </form>
            </section>

            <section>
                <SectionTitle>Gestisci convocazioni</SectionTitle>
                <div className="rounded-2xl hairline bg-card p-4 mt-3 space-y-3">
                    <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                        <SelectTrigger>
                            <SelectValue placeholder="Seleziona partita" />
                        </SelectTrigger>
                        <SelectContent>
                            {matches.map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                    {m.date} · vs {m.opponent}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectedMatch && (
                        <>
                            {players.map((p) => (
                                <label
                                    key={p.id}
                                    className="flex items-center gap-3 rounded-lg p-2 hover:bg-muted/40 press cursor-pointer"
                                >
                                    <Checkbox
                                        checked={!!selectedPlayers[p.id]}
                                        onCheckedChange={(v) =>
                                            setSelectedPlayers((s) => ({
                                                ...s,
                                                [p.id]: !!v,
                                            }))
                                        }
                                    />
                                    <div className="flex-1">
                                        <div className="font-semibold">
                                            {p.first_name} {p.last_name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {p.position || "-"}
                                        </div>
                                    </div>
                                </label>
                            ))}
                            <div className="flex justify-end">
                                <Button onClick={saveConvocations} className="press">
                                    Salva convocazioni
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </section>

            <section>
                <SectionTitle>Comunicazione alla squadra</SectionTitle>
                <form
                    onSubmit={publishComm}
                    className="rounded-2xl hairline bg-card p-4 grid gap-3 mt-3"
                >
                    <Input
                        placeholder="Titolo"
                        value={comm.title}
                        onChange={(e) => setComm({ ...comm, title: e.target.value })}
                        required
                    />
                    <Textarea
                        placeholder="Testo"
                        value={comm.body}
                        onChange={(e) => setComm({ ...comm, body: e.target.value })}
                        required
                    />
                    <Button type="submit" className="press w-fit">
                        Invia
                    </Button>
                </form>
            </section>

            <section>
                <SectionTitle>Prossime partite</SectionTitle>
                <div className="mt-3 space-y-2">
                    {matches.map((m) => (
                        <Link
                            key={m.id}
                            to={`/partite/${m.id}`}
                            className="rounded-xl hairline bg-card p-3 flex items-center gap-3 press hover:border-primary/40"
                        >
                            <div className="num text-xl font-black w-10">
                                {new Date(m.date + "T00:00:00").getDate()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">
                                    vs {m.opponent}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {m.kickoff_time} · {m.field_name || "-"}
                                </div>
                            </div>
                            <ArrowRight size={16} />
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <h2 className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight">
            {children}
        </h2>
    );
}
